import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectMainMenu, selectPermissions } from '@qtek/libs/store';
import { Permission } from '@qtek/shared/models';
import { filter, map, Observable, take, tap } from 'rxjs';
import { PermissionType } from '../models/permission.data';
import { extractFinalUrl } from '../utils/permissions.utils';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private store = inject(Store);
  private router = inject(Router);
  firstAllowedMenuItem = '';
  permissions$ = this.store
    .select(selectPermissions)
    .pipe(filter(permissions => !!permissions.size));

  checkPermission$(
    url: string | string[],
    type: PermissionType | PermissionType[]
  ): Observable<{ isAllowed: boolean; status?: string }> {
    return this.permissions$.pipe(
      map(permissions => {
        const urls = Array.isArray(url) ? url : [url];
        const typesArray = Array.isArray(type) ? type : [type];
        const typesSet = new Set(typesArray);
        const permittedRoutes = this.getPermittedFeatures(permissions, type);

        let status: string | undefined;
        let isAllowed = false;

        for (const finalUrl of urls) {
          const extractedUrl = extractFinalUrl(finalUrl);

          // Check if allowed
          const allowed = permittedRoutes.some(route =>
            route.includes(extractedUrl)
          );

          if (allowed) {
            isAllowed = true;

            // Find matching permission to get status
            if (!status) {
              for (const permission of permissions.values()) {
                const parts = permission.id.split('.');
                if (
                  parts.length >= 3 &&
                  parts[1] === extractedUrl &&
                  typesSet.has(parts[2] as PermissionType)
                ) {
                  status = permission.status;
                  break;
                }
              }
            }
          }
        }

        return { isAllowed, status };
      })
    );
  }

  isAllowed$(
    url: string | string[],
    type: PermissionType | PermissionType[]
  ): Observable<boolean> {
    return this.checkPermission$(url, type).pipe(
      map(result => result.isAllowed)
    );
  }

  getPermissionStatus$(
    url: string | string[],
    type: PermissionType | PermissionType[]
  ): Observable<string | undefined> {
    return this.checkPermission$(url, type).pipe(map(result => result.status));
  }

  isCurrentActionAllowed$(type: PermissionType): Observable<boolean> {
    const url = this.router.url;
    return this.isAllowed$(url, type);
  }

  getFirstMenuRoute(): Observable<string> {
    return this.store.select(selectMainMenu).pipe(
      take(1),
      filter(menu => !!menu.length),
      map(
        menu =>
          menu.reduce((routes, { children }) => {
            routes = [...routes, ...children.map(({ url }) => url)];
            return routes;
          }, [])[0]
      ),
      tap(firstRoute => (this.firstAllowedMenuItem = firstRoute))
    );
  }

  getPermittedFeatures(
    permissions: Map<string, Permission>,
    types: PermissionType | PermissionType[]
  ): string[] {
    const typesArray = Array.isArray(types) ? types : [types];
    const permittedRoutes = typesArray.reduce((acc, type) => {
      return acc.concat(this.getPermittedFeaturesForType(permissions, type));
    }, [] as string[]);

    return permittedRoutes;
  }

  private getPermittedFeaturesForType(
    permissions: Map<string, Permission>,
    type: PermissionType
  ): string[] {
    const routes: string[] = [];
    for (const permission of permissions.values()) {
      const parts = permission.id.split('.');
      if (parts.length >= 3 && parts[2] === type) {
        const route = parts[1];
        if (route) {
          routes.push(route);
        }
      }
    }
    return routes;
  }
}
